import * as THREE from 'three';
import { type PlayerAppearance, defaultAppearance } from '../protocol';

interface RemotePlayer {
  id: number;
  name: string;
  mesh: THREE.Group;
  targetX: number;
  targetY: number;
  targetZ: number;
  targetYaw: number;
  targetPitch: number;
  prevX: number;
  prevZ: number;
  walkTime: number;
  isMoving: boolean;
}

function createNameTag(name: string): THREE.Sprite {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;
  canvas.width = 256;
  canvas.height = 64;

  ctx.clearRect(0, 0, canvas.width, canvas.height);

  ctx.font = 'bold 32px sans-serif';
  const measured = ctx.measureText(name);
  const bgWidth = Math.min(measured.width + 30, 250);
  const bgX = (canvas.width - bgWidth) / 2;

  ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
  ctx.beginPath();
  ctx.roundRect(bgX, 6, bgWidth, 44, 8);
  ctx.fill();

  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(name, canvas.width / 2, canvas.height / 2 - 2);

  const texture = new THREE.CanvasTexture(canvas);
  texture.minFilter = THREE.LinearFilter;
  const material = new THREE.SpriteMaterial({ map: texture, depthTest: false, sizeAttenuation: true });
  const sprite = new THREE.Sprite(material);
  sprite.scale.set(2.2, 0.55, 1);
  return sprite;
}

type BoxFn = (w: number, h: number, d: number, color: number) => THREE.Mesh;

function buildBoyBody(bodyGroup: THREE.Group, v: number, box: BoxFn, appearance: PlayerAppearance) {
  const { skinColor, hairColor, shirtColor, pantsColor, shoeColor } = appearance;

  // --- Head ---
  const headGroup = new THREE.Group();
  headGroup.name = 'head';
  headGroup.add(box(3.2, 3.2, 3.2, skinColor));

  const hairTop = box(3.4, 1.0, 3.4, hairColor);
  hairTop.position.y = 1.1 * v;
  headGroup.add(hairTop);
  const hairBack = box(3.4, 2.0, 0.6, hairColor);
  hairBack.position.set(0, 0, -1.5 * v);
  headGroup.add(hairBack);

  const eyeGeo = new THREE.BoxGeometry(0.5 * v, 0.5 * v, 0.15 * v);
  const eyeMat = new THREE.MeshLambertMaterial({ color: 0x3498db });
  const leftEye = new THREE.Mesh(eyeGeo, eyeMat);
  leftEye.position.set(-0.55 * v, 0.15 * v, 1.65 * v);
  headGroup.add(leftEye);
  const rightEye = new THREE.Mesh(eyeGeo, eyeMat);
  rightEye.position.set(0.55 * v, 0.15 * v, 1.65 * v);
  headGroup.add(rightEye);

  const mouth = new THREE.Mesh(
    new THREE.BoxGeometry(1.0 * v, 0.25 * v, 0.15 * v),
    new THREE.MeshLambertMaterial({ color: 0x884444 }),
  );
  mouth.position.set(0, -0.65 * v, 1.65 * v);
  headGroup.add(mouth);

  headGroup.position.y = (4.0 + 3.2 + 1.6) * v;
  bodyGroup.add(headGroup);

  // --- Torso ---
  const torso = box(3.0, 4.0, 2.0, shirtColor);
  torso.position.y = (4.0 + 2.0) * v;
  bodyGroup.add(torso);

  // --- Arms ---
  addArms(bodyGroup, v, box, 1.0, shirtColor, skinColor);

  // --- Legs ---
  addLegs(bodyGroup, v, box, 1.2, pantsColor, shoeColor);
}

function buildGirlBody(bodyGroup: THREE.Group, v: number, box: BoxFn, appearance: PlayerAppearance) {
  const { skinColor, hairColor, shirtColor, pantsColor, shoeColor } = appearance;

  // --- Head ---
  const headGroup = new THREE.Group();
  headGroup.name = 'head';
  headGroup.add(box(3.2, 3.2, 3.2, skinColor));

  // Long hair
  const hairTop = box(3.5, 1.2, 3.5, hairColor);
  hairTop.position.y = 1.2 * v;
  headGroup.add(hairTop);
  const hairLeft = box(0.5, 3.0, 3.2, hairColor);
  hairLeft.position.set(-1.6 * v, -0.3 * v, 0);
  headGroup.add(hairLeft);
  const hairRight = box(0.5, 3.0, 3.2, hairColor);
  hairRight.position.set(1.6 * v, -0.3 * v, 0);
  headGroup.add(hairRight);
  const hairBack = box(3.5, 4.5, 0.7, hairColor);
  hairBack.position.set(0, -1.5 * v, -1.5 * v);
  headGroup.add(hairBack);

  // Eyes
  const eyeGeo = new THREE.BoxGeometry(0.5 * v, 0.5 * v, 0.15 * v);
  const eyeMat = new THREE.MeshLambertMaterial({ color: 0x3498db });
  const leftEye = new THREE.Mesh(eyeGeo, eyeMat);
  leftEye.position.set(-0.55 * v, 0.15 * v, 1.65 * v);
  headGroup.add(leftEye);
  const rightEye = new THREE.Mesh(eyeGeo, eyeMat);
  rightEye.position.set(0.55 * v, 0.15 * v, 1.65 * v);
  headGroup.add(rightEye);

  // Eyelashes
  const lashGeo = new THREE.BoxGeometry(0.6 * v, 0.15 * v, 0.15 * v);
  const lashMat = new THREE.MeshLambertMaterial({ color: 0x222222 });
  const leftLash = new THREE.Mesh(lashGeo, lashMat);
  leftLash.position.set(-0.55 * v, 0.45 * v, 1.66 * v);
  headGroup.add(leftLash);
  const rightLash = new THREE.Mesh(lashGeo, lashMat);
  rightLash.position.set(0.55 * v, 0.45 * v, 1.66 * v);
  headGroup.add(rightLash);

  // Mouth (pink)
  const mouth = new THREE.Mesh(
    new THREE.BoxGeometry(1.0 * v, 0.25 * v, 0.15 * v),
    new THREE.MeshLambertMaterial({ color: 0xcc5566 }),
  );
  mouth.position.set(0, -0.65 * v, 1.65 * v);
  headGroup.add(mouth);

  headGroup.position.y = (4.0 + 3.2 + 1.6) * v;
  bodyGroup.add(headGroup);

  // --- Torso (with skirt) ---
  const torsoUpper = box(3.0, 2.5, 2.0, shirtColor);
  torsoUpper.position.y = (4.0 + 2.75) * v;
  bodyGroup.add(torsoUpper);
  const skirt = box(3.6, 2.5, 2.4, shirtColor);
  skirt.position.y = (4.0 + 0.5) * v;
  bodyGroup.add(skirt);

  // --- Arms (narrower) ---
  addArms(bodyGroup, v, box, 0.85, shirtColor, skinColor);

  // --- Legs (narrower) ---
  addLegs(bodyGroup, v, box, 1.0, pantsColor, shoeColor);
}

function buildRobotBody(bodyGroup: THREE.Group, v: number, box: BoxFn, appearance: PlayerAppearance) {
  const { shirtColor, pantsColor, shoeColor } = appearance;
  const metalColor = 0x888899;
  const darkMetal = 0x555566;

  // --- Head (boxy, metallic) ---
  const headGroup = new THREE.Group();
  headGroup.name = 'head';
  headGroup.add(box(3.4, 3.0, 3.4, metalColor));

  // Antenna
  const antennaBase = box(0.4, 1.2, 0.4, darkMetal);
  antennaBase.position.y = 2.0 * v;
  headGroup.add(antennaBase);
  const antennaTip = box(0.6, 0.6, 0.6, 0xff3333);
  antennaTip.position.y = 2.8 * v;
  headGroup.add(antennaTip);

  // Visor eye (single glowing bar)
  const visor = new THREE.Mesh(
    new THREE.BoxGeometry(2.2 * v, 0.6 * v, 0.15 * v),
    new THREE.MeshLambertMaterial({ color: 0x00ffff, emissive: 0x00aaaa }),
  );
  visor.position.set(0, 0.2 * v, 1.8 * v);
  headGroup.add(visor);

  // Rivets on cheeks
  const rivetGeo = new THREE.BoxGeometry(0.3 * v, 0.3 * v, 0.15 * v);
  const rivetMat = new THREE.MeshLambertMaterial({ color: darkMetal });
  for (const xOff of [-1.1, 1.1]) {
    const rivet = new THREE.Mesh(rivetGeo, rivetMat);
    rivet.position.set(xOff * v, -0.5 * v, 1.8 * v);
    headGroup.add(rivet);
  }

  headGroup.position.y = (4.0 + 3.2 + 1.6) * v;
  bodyGroup.add(headGroup);

  // --- Torso (boxy with panel lines) ---
  const torso = box(3.2, 4.0, 2.2, metalColor);
  torso.position.y = (4.0 + 2.0) * v;
  bodyGroup.add(torso);
  // Chest panel
  const panel = box(2.0, 2.0, 0.15, shirtColor);
  panel.position.set(0, (4.0 + 2.2) * v, 1.18 * v);
  bodyGroup.add(panel);

  // --- Arms (boxy metal) ---
  addArms(bodyGroup, v, box, 1.1, metalColor, darkMetal);

  // --- Legs (boxy metal) ---
  addLegs(bodyGroup, v, box, 1.3, pantsColor, shoeColor);
}

function buildSkeletonBody(bodyGroup: THREE.Group, v: number, box: BoxFn, appearance: PlayerAppearance) {
  const boneColor = 0xeeeedd;
  const darkBone = 0xccccbb;

  // --- Skull head ---
  const headGroup = new THREE.Group();
  headGroup.name = 'head';
  headGroup.add(box(3.2, 3.2, 3.2, boneColor));

  // Dark eye sockets
  const eyeGeo = new THREE.BoxGeometry(0.7 * v, 0.7 * v, 0.2 * v);
  const eyeMat = new THREE.MeshLambertMaterial({ color: 0x111111 });
  const leftEye = new THREE.Mesh(eyeGeo, eyeMat);
  leftEye.position.set(-0.55 * v, 0.2 * v, 1.7 * v);
  headGroup.add(leftEye);
  const rightEye = new THREE.Mesh(eyeGeo, eyeMat);
  rightEye.position.set(0.55 * v, 0.2 * v, 1.7 * v);
  headGroup.add(rightEye);

  // Nose hole
  const nose = new THREE.Mesh(
    new THREE.BoxGeometry(0.3 * v, 0.35 * v, 0.15 * v),
    new THREE.MeshLambertMaterial({ color: 0x222222 }),
  );
  nose.position.set(0, -0.2 * v, 1.7 * v);
  headGroup.add(nose);

  // Teeth grin
  const teethGeo = new THREE.BoxGeometry(1.6 * v, 0.4 * v, 0.15 * v);
  const teethMat = new THREE.MeshLambertMaterial({ color: boneColor });
  const teeth = new THREE.Mesh(teethGeo, teethMat);
  teeth.position.set(0, -0.7 * v, 1.7 * v);
  headGroup.add(teeth);
  // Teeth gaps (dark lines)
  for (const xOff of [-0.5, 0, 0.5]) {
    const gap = new THREE.Mesh(
      new THREE.BoxGeometry(0.08 * v, 0.4 * v, 0.16 * v),
      new THREE.MeshLambertMaterial({ color: 0x111111 }),
    );
    gap.position.set(xOff * v, -0.7 * v, 1.73 * v);
    headGroup.add(gap);
  }

  headGroup.position.y = (4.0 + 3.2 + 1.6) * v;
  bodyGroup.add(headGroup);

  // --- Torso (ribcage – thin) ---
  const torso = box(2.6, 4.0, 1.6, darkBone);
  torso.position.y = (4.0 + 2.0) * v;
  bodyGroup.add(torso);
  // Rib lines
  for (let i = 0; i < 3; i++) {
    const rib = box(2.8, 0.3, 0.15, boneColor);
    rib.position.set(0, (4.0 + 3.0 - i * 1.0) * v, 0.9 * v);
    bodyGroup.add(rib);
  }

  // --- Arms (thin bones) ---
  addArms(bodyGroup, v, box, 0.7, darkBone, boneColor);

  // --- Legs (thin bones) ---
  addLegs(bodyGroup, v, box, 0.8, darkBone, boneColor);
}

function buildAlienBody(bodyGroup: THREE.Group, v: number, box: BoxFn, appearance: PlayerAppearance) {
  const { shirtColor, pantsColor, shoeColor } = appearance;
  const skinGreen = 0x66cc66;

  // --- Big head ---
  const headGroup = new THREE.Group();
  headGroup.name = 'head';
  // Larger, taller head
  headGroup.add(box(3.8, 3.8, 3.4, skinGreen));

  // Huge black oval eyes
  const eyeGeo = new THREE.BoxGeometry(1.0 * v, 1.4 * v, 0.15 * v);
  const eyeMat = new THREE.MeshLambertMaterial({ color: 0x111111 });
  const leftEye = new THREE.Mesh(eyeGeo, eyeMat);
  leftEye.position.set(-0.7 * v, 0.2 * v, 1.8 * v);
  headGroup.add(leftEye);
  const rightEye = new THREE.Mesh(eyeGeo, eyeMat);
  rightEye.position.set(0.7 * v, 0.2 * v, 1.8 * v);
  headGroup.add(rightEye);

  // Small mouth slit
  const mouth = new THREE.Mesh(
    new THREE.BoxGeometry(0.6 * v, 0.15 * v, 0.15 * v),
    new THREE.MeshLambertMaterial({ color: 0x338833 }),
  );
  mouth.position.set(0, -0.8 * v, 1.8 * v);
  headGroup.add(mouth);

  // Antennae
  for (const xOff of [-0.8, 0.8]) {
    const stem = box(0.2, 1.5, 0.2, skinGreen);
    stem.position.set(xOff * v, 2.5 * v, 0);
    headGroup.add(stem);
    const tip = box(0.4, 0.4, 0.4, 0x44ff44);
    tip.position.set(xOff * v, 3.4 * v, 0);
    headGroup.add(tip);
  }

  headGroup.position.y = (4.0 + 3.2 + 1.6) * v;
  bodyGroup.add(headGroup);

  // --- Torso (thin) ---
  const torso = box(2.4, 4.0, 1.6, shirtColor);
  torso.position.y = (4.0 + 2.0) * v;
  bodyGroup.add(torso);

  // --- Arms (thin) ---
  addArms(bodyGroup, v, box, 0.7, shirtColor, skinGreen);

  // --- Legs (thin) ---
  addLegs(bodyGroup, v, box, 0.9, pantsColor, shoeColor);
}

function buildPumpkinBody(bodyGroup: THREE.Group, v: number, box: BoxFn, appearance: PlayerAppearance) {
  const { pantsColor, shoeColor } = appearance;
  const orangeColor = 0xe87400;
  const darkOrange = 0xcc5500;
  const greenShirt = 0x3a7a2a;

  // --- Pumpkin head ---
  const headGroup = new THREE.Group();
  headGroup.name = 'head';
  // Round-ish (slightly wider/taller)
  headGroup.add(box(3.8, 3.4, 3.8, orangeColor));

  // Vertical ridges
  for (const xOff of [-1.4, 0, 1.4]) {
    const ridge = box(0.15, 3.2, 0.15, darkOrange);
    ridge.position.set(xOff * v, 0, 2.0 * v);
    headGroup.add(ridge);
  }

  // Carved face - triangle eyes
  const eyeGeo = new THREE.BoxGeometry(0.7 * v, 0.7 * v, 0.2 * v);
  const carvedMat = new THREE.MeshLambertMaterial({ color: 0xffcc00, emissive: 0xaa7700 });
  const leftEye = new THREE.Mesh(eyeGeo, carvedMat);
  leftEye.position.set(-0.6 * v, 0.3 * v, 2.0 * v);
  headGroup.add(leftEye);
  const rightEye = new THREE.Mesh(eyeGeo, carvedMat);
  rightEye.position.set(0.6 * v, 0.3 * v, 2.0 * v);
  headGroup.add(rightEye);

  // Carved mouth (wide jagged grin)
  const mouthGeo = new THREE.BoxGeometry(1.8 * v, 0.5 * v, 0.2 * v);
  const carvedMouth = new THREE.Mesh(mouthGeo, carvedMat);
  carvedMouth.position.set(0, -0.6 * v, 2.0 * v);
  headGroup.add(carvedMouth);

  // Leaf/stem on top
  const stem = box(0.3, 0.8, 0.3, 0x2a5a1a);
  stem.position.y = 2.0 * v;
  headGroup.add(stem);
  const leaf = box(1.0, 0.15, 0.5, 0x44aa22);
  leaf.position.set(0.4 * v, 2.0 * v, 0);
  leaf.rotation.z = 0.3;
  headGroup.add(leaf);

  headGroup.position.y = (4.0 + 3.2 + 1.6) * v;
  bodyGroup.add(headGroup);

  // --- Torso (green shirt) ---
  const torso = box(3.0, 4.0, 2.0, greenShirt);
  torso.position.y = (4.0 + 2.0) * v;
  bodyGroup.add(torso);

  // --- Arms ---
  addArms(bodyGroup, v, box, 1.0, greenShirt, orangeColor);

  // --- Legs ---
  addLegs(bodyGroup, v, box, 1.2, pantsColor, shoeColor);
}

function buildNinjaBody(bodyGroup: THREE.Group, v: number, box: BoxFn, appearance: PlayerAppearance) {
  const { skinColor } = appearance;
  const darkCloth = 0x1a1a2e;
  const accentColor = 0x333355;

  // --- Head (wrapped mask, only eyes visible) ---
  const headGroup = new THREE.Group();
  headGroup.name = 'head';
  headGroup.add(box(3.2, 3.2, 3.2, darkCloth));

  // Head wrap band
  const headband = box(3.4, 0.5, 3.4, accentColor);
  headband.position.y = 0.8 * v;
  headGroup.add(headband);

  // Eyes slit (narrow opening showing skin)
  const eyeSlit = box(2.6, 0.5, 0.15, skinColor);
  eyeSlit.position.set(0, 0.15 * v, 1.7 * v);
  headGroup.add(eyeSlit);

  // Eye pupils
  const eyeGeo = new THREE.BoxGeometry(0.35 * v, 0.35 * v, 0.16 * v);
  const eyeMat = new THREE.MeshLambertMaterial({ color: 0x222222 });
  const leftEye = new THREE.Mesh(eyeGeo, eyeMat);
  leftEye.position.set(-0.5 * v, 0.15 * v, 1.78 * v);
  headGroup.add(leftEye);
  const rightEye = new THREE.Mesh(eyeGeo, eyeMat);
  rightEye.position.set(0.5 * v, 0.15 * v, 1.78 * v);
  headGroup.add(rightEye);

  // Trailing cloth (back of head wrap)
  const trail = box(1.2, 2.5, 0.4, darkCloth);
  trail.position.set(0, -0.5 * v, -1.7 * v);
  headGroup.add(trail);

  headGroup.position.y = (4.0 + 3.2 + 1.6) * v;
  bodyGroup.add(headGroup);

  // --- Torso (slim, dark) ---
  const torso = box(2.8, 4.0, 1.8, darkCloth);
  torso.position.y = (4.0 + 2.0) * v;
  bodyGroup.add(torso);
  // Belt/sash
  const belt = box(3.0, 0.5, 2.0, accentColor);
  belt.position.y = (4.0 + 1.0) * v;
  bodyGroup.add(belt);

  // --- Arms (slim) ---
  addArms(bodyGroup, v, box, 0.85, darkCloth, skinColor);

  // --- Legs (slim) ---
  addLegs(bodyGroup, v, box, 1.0, darkCloth, 0x111111);
}

// --- Shared arm/leg helpers ---

function addArms(bodyGroup: THREE.Group, v: number, box: BoxFn, armWidth: number, clothColor: number, handColor: number) {
  const leftArmPivot = new THREE.Group();
  leftArmPivot.name = 'leftArm';
  leftArmPivot.add(box(armWidth, 4.0, armWidth, clothColor));
  const leftHand = box(armWidth, 1.0, armWidth, handColor);
  leftHand.position.y = -2.5 * v;
  leftArmPivot.add(leftHand);
  leftArmPivot.position.set(-2.0 * v, (4.0 + 3.5) * v, 0);
  bodyGroup.add(leftArmPivot);

  const rightArmPivot = new THREE.Group();
  rightArmPivot.name = 'rightArm';
  rightArmPivot.add(box(armWidth, 4.0, armWidth, clothColor));
  const rightHand = box(armWidth, 1.0, armWidth, handColor);
  rightHand.position.y = -2.5 * v;
  rightArmPivot.add(rightHand);
  rightArmPivot.position.set(2.0 * v, (4.0 + 3.5) * v, 0);
  bodyGroup.add(rightArmPivot);
}

function addLegs(bodyGroup: THREE.Group, v: number, box: BoxFn, legWidth: number, legColor: number, shoeColor: number) {
  const leftLegPivot = new THREE.Group();
  leftLegPivot.name = 'leftLeg';
  const leftLeg = box(legWidth, 3.5, legWidth, legColor);
  leftLeg.position.y = -1.75 * v;
  leftLegPivot.add(leftLeg);
  const leftShoe = box(legWidth, 0.8, 1.4, shoeColor);
  leftShoe.position.set(0, -3.75 * v, 0.1 * v);
  leftLegPivot.add(leftShoe);
  leftLegPivot.position.set(-0.6 * v, 4.0 * v, 0);
  bodyGroup.add(leftLegPivot);

  const rightLegPivot = new THREE.Group();
  rightLegPivot.name = 'rightLeg';
  const rightLeg = box(legWidth, 3.5, legWidth, legColor);
  rightLeg.position.y = -1.75 * v;
  rightLegPivot.add(rightLeg);
  const rightShoe = box(legWidth, 0.8, 1.4, shoeColor);
  rightShoe.position.set(0, -3.75 * v, 0.1 * v);
  rightLegPivot.add(rightShoe);
  rightLegPivot.position.set(0.6 * v, 4.0 * v, 0);
  bodyGroup.add(rightLegPivot);
}

export function buildPlayerMesh(name: string, appearance: PlayerAppearance): THREE.Group {
  const root = new THREE.Group();
  const v = 0.25;

  function box(w: number, h: number, d: number, color: number): THREE.Mesh {
    return new THREE.Mesh(
      new THREE.BoxGeometry(w * v, h * v, d * v),
      new THREE.MeshLambertMaterial({ color }),
    );
  }

  const bodyGroup = new THREE.Group();
  bodyGroup.name = 'bodyGroup';
  root.add(bodyGroup);

  const modelType = appearance.modelType ?? 0;
  switch (modelType) {
    case 1: buildGirlBody(bodyGroup, v, box, appearance); break;
    case 2: buildRobotBody(bodyGroup, v, box, appearance); break;
    case 3: buildSkeletonBody(bodyGroup, v, box, appearance); break;
    case 4: buildAlienBody(bodyGroup, v, box, appearance); break;
    case 5: buildPumpkinBody(bodyGroup, v, box, appearance); break;
    case 6: buildNinjaBody(bodyGroup, v, box, appearance); break;
    default: buildBoyBody(bodyGroup, v, box, appearance); break;
  }

  // --- Name tag ---
  if (name) {
    const nameTag = createNameTag(name);
    nameTag.position.y = (4.0 + 3.2 + 3.6 + 1.6) * v;
    nameTag.name = 'nameTag';
    root.add(nameTag);
  }

  return root;
}

function getPartByName(group: THREE.Group, name: string): THREE.Group | null {
  let result: THREE.Group | null = null;
  group.traverse((child) => {
    if (child.name === name && child instanceof THREE.Group) {
      result = child;
    }
  });
  return result;
}

export class RemotePlayers {
  private players = new Map<number, RemotePlayer>();
  private scene: THREE.Scene;
  private localPlayerId = -1;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  setLocalPlayerId(id: number) {
    this.localPlayerId = id;
  }

  addPlayer(id: number, name: string, appearance?: PlayerAppearance) {
    if (id === this.localPlayerId) return;
    if (this.players.has(id)) return;

    const group = buildPlayerMesh(name, appearance || defaultAppearance(id));

    this.scene.add(group);

    this.players.set(id, {
      id,
      name,
      mesh: group,
      targetX: 0,
      targetY: 0,
      targetZ: 0,
      targetYaw: 0,
      targetPitch: 0,
      prevX: 0,
      prevZ: 0,
      walkTime: 0,
      isMoving: false,
    });
  }

  removePlayer(id: number) {
    const p = this.players.get(id);
    if (!p) return;
    this.scene.remove(p.mesh);
    p.mesh.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose();
        if (child.material instanceof THREE.Material) child.material.dispose();
      }
      if (child instanceof THREE.Sprite) {
        if (child.material.map) child.material.map.dispose();
        child.material.dispose();
      }
    });
    this.players.delete(id);
  }

  updatePositions(positions: { id: number; x: number; y: number; z: number; yaw: number; pitch: number }[]) {
    for (const pos of positions) {
      if (pos.id === this.localPlayerId) continue;
      const p = this.players.get(pos.id);
      if (!p) continue;
      p.targetX = pos.x;
      p.targetY = pos.y;
      p.targetZ = pos.z;
      p.targetYaw = pos.yaw;
      p.targetPitch = pos.pitch;
    }
  }

  update(dt: number) {
    const lerpFactor = Math.min(1, dt * 10);
    for (const p of this.players.values()) {
      // Interpolate position
      p.mesh.position.x += (p.targetX - p.mesh.position.x) * lerpFactor;
      p.mesh.position.y += (p.targetY - p.mesh.position.y) * lerpFactor;
      p.mesh.position.z += (p.targetZ - p.mesh.position.z) * lerpFactor;

      // Detect movement for walk animation
      const dx = p.mesh.position.x - p.prevX;
      const dz = p.mesh.position.z - p.prevZ;
      const dist = Math.sqrt(dx * dx + dz * dz);
      p.isMoving = dist > 0.01;
      p.prevX = p.mesh.position.x;
      p.prevZ = p.mesh.position.z;

      // Rotate body to face movement direction
      // Player yaw: 0 = looking toward -Z, but model face is toward +Z, so add PI
      const bodyGroup = getPartByName(p.mesh, 'bodyGroup');
      if (bodyGroup) {
        bodyGroup.rotation.y = p.targetYaw + Math.PI;
      }

      // Animate limbs
      const leftArm = getPartByName(p.mesh, 'leftArm');
      const rightArm = getPartByName(p.mesh, 'rightArm');
      const leftLeg = getPartByName(p.mesh, 'leftLeg');
      const rightLeg = getPartByName(p.mesh, 'rightLeg');

      if (p.isMoving) {
        p.walkTime += dt;
        const swingAngle = Math.sin(p.walkTime * 5) * 0.6;
        if (leftArm) leftArm.rotation.x = swingAngle;
        if (rightArm) rightArm.rotation.x = -swingAngle;
        if (leftLeg) leftLeg.rotation.x = -swingAngle * 0.7;
        if (rightLeg) rightLeg.rotation.x = swingAngle * 0.7;
      } else {
        // Dampen back to idle
        if (leftArm) leftArm.rotation.x *= 0.9;
        if (rightArm) rightArm.rotation.x *= 0.9;
        if (leftLeg) leftLeg.rotation.x *= 0.9;
        if (rightLeg) rightLeg.rotation.x *= 0.9;
      }

      // Idle bob
      const bobAmount = p.isMoving ? 0.03 : 0.06;
      const bobSpeed = p.isMoving ? 5 : 2;
      const bob = Math.sin(p.walkTime * bobSpeed) * bobAmount;
      p.mesh.position.y += bob;
    }
  }

  getPlayerList(): { id: number; name: string; x: number; y: number; z: number }[] {
    const list: { id: number; name: string; x: number; y: number; z: number }[] = [];
    for (const p of this.players.values()) {
      list.push({
        id: p.id,
        name: p.name,
        x: Math.floor(p.mesh.position.x),
        y: Math.floor(p.mesh.position.y),
        z: Math.floor(p.mesh.position.z),
      });
    }
    return list.sort((a, b) => a.name.localeCompare(b.name));
  }

  dispose() {
    for (const p of this.players.values()) {
      this.scene.remove(p.mesh);
      p.mesh.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose();
          if (child.material instanceof THREE.Material) child.material.dispose();
        }
        if (child instanceof THREE.Sprite) {
          if (child.material.map) child.material.map.dispose();
          child.material.dispose();
        }
      });
    }
    this.players.clear();
  }
}
